import { lerp, lerpPoint, MAP_EXIT_MS, smoothstep } from "./phaseTransition";

/** Apparitions, disparitions, éclatements et regroupements des unités de la carte, image par image (fonction pure
 *  tirée de `UnitsLayers`, pour être vérifiable). Une unité qui entre se fond ; un subordonné qui entre (zoom) éclate
 *  depuis la position affichée de son plus proche chef encore à l'écran ; une unité qui sort s'efface, et un
 *  subordonné qui sort (dézoom) glisse en s'effaçant vers son plus proche chef à l'écran. Affichage exclusif des
 *  niveaux (registre J13) : une fois la transition finie, seules les unités de la profondeur affichée restent ; une
 *  formation plus haute que rien ne détaille (`ghost`) passe en douceur à un repère discret, et en revient. */

type Point = [number, number];

/** Éclatement d'une unité en ses subordonnés (ou regroupement) : glissement depuis (vers) la position du chef. */
export const LEVEL_SLIDE_MS = 650;
/** Changement de niveau sans « creux » (registre J16 : au fondu croisé symétrique, chef et subordonnés passaient
 *  ensemble sous 50 % d'opacité, la carte semblait clignoter) : les arrivants se fondent vite, les partants
 *  lentement ; la somme reste visible à chaque image. */
export const LEVEL_ENTER_MS = 300;
/** Croissance d'un subordonné qui éclate de son chef (et décroissance quand il s'y regroupe) : taille de départ. */
export const GROW_FROM = 0.55;
/** Chef qui laisse place à ses subordonnés (ou qui revient) : taille au bout de sa sortie (de départ à son retour). */
export const CHIEF_SHRINK = 0.8;
/** Apparition d'une entité (unité ou symbole) hors transition de temps. */
export const ENTITY_ENTER_MS = MAP_EXIT_MS;
/** Repère discret d'une formation que rien ne détaille à la profondeur affichée : opacité et taille du symbole. */
export const GHOST_OPACITY = 0.35;
export const GHOST_SIZE = 0.6;

/** Fondu d'une entité : de `from` à `to` (opacité propre) en `duration` ms depuis `started`. */
export interface EntityFade {
  from: number;
  to: number;
  started: number;
  duration: number;
}

/** Opacité propre d'une entité à l'instant `now` (1 sans fondu en cours), courbe douce. */
export function fadeAt(fade: EntityFade | undefined, now: number): number {
  if (!fade) return 1;
  const raw = Math.min(1, Math.max(0, (now - fade.started) / Math.max(1, fade.duration)));
  return Math.min(1, Math.max(0, lerp(fade.from, fade.to, smoothstep(raw))));
}

export function fadeDone(fade: EntityFade | undefined, now: number): boolean {
  return !fade || now - fade.started >= fade.duration;
}

/** Chefs possibles d'une unité d'un niveau inférieur, du plus proche au plus lointain : les clés d'affichage
 *  « <chef>><unité> » s'emboîtent (« 1DIV>B1>G1 » → « 1DIV>B1 », « 1DIV »). Une clé ennemie (« ENI><code> ») n'en a
 *  aucun. */
export function ancestorKeys(key: string): string[] {
  if (key.startsWith("ENI>")) return [];
  const parts = key.split(">");
  const out: string[] = [];
  for (let n = parts.length - 1; n > 0; n--) out.push(parts.slice(0, n).join(">"));
  return out;
}

interface Leaving {
  at: Point;
  properties: Record<string, unknown>;
  /** Unité qui la regroupe (dézoom) : la sortante glisse vers sa position en s'effaçant. */
  toward: string | null;
}

/** Mémoire d'une image à l'autre. */
export interface EntityMemory {
  displayed: Map<string, { at: Point; opacity: number }>;
  fades: Map<string, EntityFade>;
  leaving: Map<string, Leaving>;
  present: Set<string>;
  lastProperties: Map<string, Record<string, unknown>>;
  slides: Map<string, { from: Point; started: number }>;
  /** Repère discret : valeur affichée (0 plein, 1 repère) et passage en cours. */
  ghosts: Map<string, { value: number; fade?: EntityFade }>;
  /** Taille relative en cours de changement (croissance depuis le chef, décroissance vers lui). */
  sizes: Map<string, EntityFade>;
}

export function entityMemory(): EntityMemory {
  return {
    displayed: new Map(),
    fades: new Map(),
    leaving: new Map(),
    present: new Set(),
    lastProperties: new Map(),
    slides: new Map(),
    ghosts: new Map(),
    sizes: new Map(),
  };
}

/** Unité à l'écran à cette image : position visée (transition de temps comprise), subordonné ou non, repère. */
export interface EntityInput {
  key: string;
  at: Point;
  /** Unité d'un niveau inférieur : elle éclate de son chef à l'entrée et glisse vers lui à la sortie. */
  sub: boolean;
  ghost: boolean;
  properties: Record<string, unknown>;
}

export interface EntityOutput {
  key: string;
  at: Point;
  /** Opacité propre (fondu d'entrée ou de sortie), sans le repère discret. */
  opacity: number;
  /** 0 : symbole plein ; 1 : repère discret ; entre les deux pendant le passage. */
  ghost: number;
  /** Taille relative du symbole (1 hors changement de niveau). */
  size: number;
  leaving: boolean;
  properties: Record<string, unknown>;
}

/** Image suivante : met à jour `memory` et rend les entités à dessiner (entrantes, présentes, sortantes). */
export function stepEntities(
  memory: EntityMemory,
  inputs: readonly EntityInput[],
  now: number,
): { entities: EntityOutput[]; animating: boolean } {
  const shown = new Map<string, { at: Point; opacity: number }>();
  const entities: EntityOutput[] = [];
  const visible = new Set<string>(inputs.map((i) => i.key));
  // Unités qui quittent l'écran à cette image : leurs chefs (regroupement) reviennent en grandissant.
  const departing = [...memory.present].filter((key) => !visible.has(key) && !memory.leaving.has(key));
  const regrouping = new Set(departing.flatMap((key) => ancestorKeys(key)));
  for (const input of inputs) {
    const { key } = input;
    let at = input.at;
    if (!memory.present.has(key)) {
      // Unité qui apparaît (ou qui revient pendant sa sortie) : fondu depuis l'opacité affichée ; subordonné qui
      // éclate (zoom) : il part de la position affichée de son plus proche chef, en grandissant.
      // Unité rappelée pendant sa sortie (l'utilisateur rezoome avant la fin du regroupement) : elle repart de là où
      // elle est à l'écran, jamais de la position de son chef (registre J14 : elle y sautait).
      const returning = memory.leaving.has(key) ? memory.displayed.get(key) : undefined;
      const from = memory.leaving.has(key) ? fadeAt(memory.fades.get(key), now) : 0;
      const size = memory.sizes.has(key) ? fadeAt(memory.sizes.get(key), now) : 1;
      memory.leaving.delete(key);
      const chief = returning
        ? returning
        : input.sub
          ? ancestorKeys(key)
              .map((k) => memory.displayed.get(k))
              .find(Boolean)
          : undefined;
      const level = Boolean(chief) || regrouping.has(key);
      memory.fades.set(key, { from, to: 1, started: now, duration: level ? LEVEL_ENTER_MS : ENTITY_ENTER_MS });
      if (chief) memory.slides.set(key, { from: chief.at, started: now });
      if (chief && !returning) memory.sizes.set(key, { from: GROW_FROM, to: 1, started: now, duration: LEVEL_SLIDE_MS });
      else if (returning) memory.sizes.set(key, { from: size, to: 1, started: now, duration: LEVEL_ENTER_MS });
      else if (regrouping.has(key)) memory.sizes.set(key, { from: CHIEF_SHRINK, to: 1, started: now, duration: LEVEL_SLIDE_MS });
    }
    const slide = memory.slides.get(key);
    if (slide) {
      const progress = (now - slide.started) / LEVEL_SLIDE_MS;
      if (progress >= 1) memory.slides.delete(key);
      else at = lerpPoint(slide.from, at, smoothstep(Math.max(0, progress)));
    }
    const fade = memory.fades.get(key);
    const opacity = fadeAt(fade, now);
    if (fade && fadeDone(fade, now)) memory.fades.delete(key);
    const ghost = ghostAt(memory, key, input.ghost ? 1 : 0, now);
    const size = sizeAt(memory, key, now);
    shown.set(key, { at, opacity });
    entities.push({ key, at, opacity, ghost, size, leaving: false, properties: input.properties });
  }
  // Unités masquées ou retirées : gardées à leur dernière place, opacité décroissante, pendant `MAP_EXIT_MS` ;
  // subordonné regroupé (dézoom) : il glisse vers son plus proche chef revenu à l'écran en s'effaçant et en
  // rapetissant ; chef qui laisse place à ses subordonnés (zoom) : il s'efface lentement sur place en rapetissant,
  // pendant que ses subordonnés, déjà visibles, éclatent de lui (aucun creux).
  const detailed = departing.length ? new Set(inputs.flatMap((i) => (i.sub ? ancestorKeys(i.key) : []))) : new Set<string>();
  for (const key of departing) {
    const last = memory.displayed.get(key);
    const properties = memory.lastProperties.get(key);
    if (!last || !properties) continue;
    const toward = ancestorKeys(key).find((k) => visible.has(k)) ?? null;
    const chief = detailed.has(key);
    const size = sizeAt(memory, key, now);
    memory.leaving.set(key, { at: last.at, properties, toward });
    memory.fades.set(key, {
      from: last.opacity,
      to: 0,
      started: now,
      duration: toward || chief ? LEVEL_SLIDE_MS : MAP_EXIT_MS,
    });
    if (toward) memory.sizes.set(key, { from: size, to: GROW_FROM, started: now, duration: LEVEL_SLIDE_MS });
    else if (chief) memory.sizes.set(key, { from: size, to: CHIEF_SHRINK, started: now, duration: LEVEL_SLIDE_MS });
    memory.slides.delete(key);
  }
  for (const [key, item] of memory.leaving) {
    const fade = memory.fades.get(key);
    const opacity = fadeAt(fade, now);
    if (fadeDone(fade, now) || opacity <= 0) {
      memory.leaving.delete(key);
      memory.fades.delete(key);
      memory.ghosts.delete(key);
      memory.sizes.delete(key);
      continue;
    }
    const goal = item.toward ? shown.get(item.toward)?.at : undefined;
    const progress = fade ? Math.min(1, Math.max(0, (now - fade.started) / Math.max(1, fade.duration))) : 1;
    const at = goal ? lerpPoint(item.at, goal, smoothstep(progress)) : item.at;
    shown.set(key, { at, opacity });
    entities.push({
      key,
      at,
      opacity,
      ghost: memory.ghosts.get(key)?.value ?? 0,
      size: sizeAt(memory, key, now),
      leaving: true,
      properties: item.properties,
    });
  }
  for (const key of memory.ghosts.keys()) {
    if (!visible.has(key) && !memory.leaving.has(key)) memory.ghosts.delete(key);
  }
  memory.present = visible;
  memory.displayed = shown;
  memory.lastProperties = new Map(entities.filter((e) => !e.leaving).map((e) => [e.key, e.properties]));
  const ghosting = [...memory.ghosts.values()].some((g) => g.fade);
  return { entities, animating: memory.fades.size > 0 || memory.slides.size > 0 || memory.sizes.size > 0 || ghosting };
}

/** Taille relative à l'instant `now` (1 sans changement en cours) ; le changement fini, elle est oubliée (sauf pour
 *  une sortante, qui garde sa taille finale jusqu'à son départ). */
function sizeAt(memory: EntityMemory, key: string, now: number): number {
  const change = memory.sizes.get(key);
  if (!change) return 1;
  const value = fadeAt(change, now);
  if (fadeDone(change, now) && change.to === 1) memory.sizes.delete(key);
  return value;
}

/** Valeur du repère discret à l'instant `now` : une unité qui entre le prend tel quel (elle se fond déjà) ; une
 *  unité présente qui devient (ou cesse d'être) un repère y passe en `LEVEL_SLIDE_MS`. */
function ghostAt(memory: EntityMemory, key: string, wanted: number, now: number): number {
  const state = memory.ghosts.get(key);
  if (!state) {
    memory.ghosts.set(key, { value: wanted });
    return wanted;
  }
  const goal = state.fade ? state.fade.to : state.value;
  if (goal !== wanted) {
    const current = state.fade ? lerp(state.fade.from, state.fade.to, smoothstep(progressOf(state.fade, now))) : state.value;
    state.fade = { from: current, to: wanted, started: now, duration: LEVEL_SLIDE_MS };
  }
  if (state.fade) {
    state.value = lerp(state.fade.from, state.fade.to, smoothstep(progressOf(state.fade, now)));
    if (fadeDone(state.fade, now)) {
      state.value = state.fade.to;
      state.fade = undefined;
    }
  }
  return state.value;
}

function progressOf(fade: EntityFade, now: number): number {
  return Math.min(1, Math.max(0, (now - fade.started) / Math.max(1, fade.duration)));
}
